import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { TypeAheadFilter } from '@components/table/filters/TypeAheadFilter';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';

import {
  TSubjectivityLibraryItem,
  useSearchPaginatedSubjectivityLibrary,
} from '@services/subjectivity/subjectivity-library/useSearchPaginatedSubjectivityLibrary';

import { handleScrollToBottom } from '@utils/HandleScrollToBottom';

export const SubjectivityLibraryTypeAhead = ({
  onSelectFromList,
  onChangeInput,
  defaultValue,
  mode = 'single',
  selectedItems = [],
  availableLOIIds,
}: {
  onSelectFromList: (value: string | TSubjectivityLibraryItem | TSubjectivityLibraryItem[]) => void;
  onChangeInput?: (value: string) => void;
  defaultValue?: string;
  mode?: 'single' | 'multiple';
  selectedItems?: TSubjectivityLibraryItem[];
  availableLOIIds?: string[];
}) => {
  const [searchText, setSearchText] = useState('');
  const [isDirty, setIsDirty] = useState(mode === 'multiple');

  const hideFromList = selectedItems ? selectedItems.map((item) => item.id) : [];
  const {
    items: searchItems,
    fetchNextPage,
    isLoading,
    isFetching,
    isPending,
    isFetchingNextPage,
  } = useSearchPaginatedSubjectivityLibrary({
    params: { searchText, insuranceLineId: availableLOIIds },
    enabled: true,
  });

  const handleDebouncedInputChange = useDebouncedCallback((value) => {
    setSearchText(value);
  }, DEBOUNCE_DELAYS.FORM_TYPING);

  const items =
    searchItems
      ?.filter((ip) => !hideFromList?.includes(ip.id))
      ?.map((ip) => {
        return {
          ...ip,
          key: ip.id,
          value: ip.id,
          label: ip.name,
        };
      }) ?? [];

  const isLoadingMore = isLoading || isFetching || isPending || isFetchingNextPage;

  return (
    <TypeAheadFilter
      required={true}
      label={mode === 'multiple' ? 'Search Library' : 'Label'}
      defaultValue={defaultValue}
      value={mode === 'multiple' ? selectedItems : undefined}
      options={isDirty ? (items ?? []) : []}
      isMultiple={mode === 'multiple'}
      limitTags={1}
      onChange={(value: string | TSubjectivityLibraryItem | TSubjectivityLibraryItem[]) => {
        onSelectFromList(value);
      }}
      onInputChange={(event, value) => {
        setIsDirty(true);
        handleDebouncedInputChange(value);
        onChangeInput?.(value);
      }}
      freeSolo={mode !== 'multiple'}
      hideTags={mode === 'multiple'}
      hideCheckboxes={mode === 'multiple'}
      disableClearable
      highlightSearchText
      loading={isLoadingMore}
      slotProps={{
        listbox: {
          onScroll: (e) => {
            handleScrollToBottom(e, fetchNextPage);
          },
          style: {
            maxHeight: '300px',
            backgroundColor: 'background.light',
          },
        },
      }}
    />
  );
};
